/**
 * Admin Project Context Persistence
 *
 * Stores the admin's cache snapshot in a separate IndexedDB database (via Dexie)
 * to avoid localStorage quota issues. Uses its own database to keep it decoupled
 * from the worker data in WorkerPortalDB.
 *
 * The snapshot is restored when the admin returns to SelectProject or WorkerStatus
 * after a worker session overwrites the Redux cache.
 *
 * The saved context is cleared only on explicit admin logout.
 */

import Dexie, { Table } from 'dexie';

const ADMIN_CONTEXT_KEY = 'adminProjectContext';

export interface IAdminContext {
  cache: Record<string, any>;
  savedAt: string;
}

interface IAdminContextRecord extends IAdminContext {
  key: string;
}

const adminContextDb = new Dexie('AdminContextDB');
adminContextDb.version(1).stores({ context: 'key' });

const contextTable = (adminContextDb as Dexie & { context: Table<IAdminContextRecord, string> }).context;

/**
 * Save the admin's current project context (full cache state).
 * Called after loadAllData succeeds on the SelectProject screen for non-worker users.
 */
export const saveAdminContext = async (cacheState: Record<string, any>): Promise<void> => {
  try {
    await contextTable.put({
      key: ADMIN_CONTEXT_KEY,
      cache: cacheState,
      savedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('[adminContext] Failed to save admin context:', error);
  }
};

/**
 * Retrieve the previously saved admin project context, if any.
 */
export const getAdminContext = async (): Promise<IAdminContext | null> => {
  try {
    const record = await contextTable.get(ADMIN_CONTEXT_KEY);
    if (!record) return null;
    return { cache: record.cache, savedAt: record.savedAt };
  } catch (error) {
    console.error('[adminContext] Failed to read admin context:', error);
    return null;
  }
};

/**
 * Check whether a saved admin context exists.
 */
export const hasAdminContext = async (): Promise<boolean> => {
  try {
    const count = await contextTable.where('key').equals(ADMIN_CONTEXT_KEY).count();
    return count > 0;
  } catch {
    return false;
  }
};

/**
 * Get only the saved project ID without reading the full cache.
 * Lightweight check used in AppRoot to decide if an access check is needed.
 */
export const getAdminContextProjectId = async (): Promise<string | null> => {
  try {
    const record = await contextTable.get(ADMIN_CONTEXT_KEY);
    return record?.cache?.project?.id || null;
  } catch {
    return null;
  }
};

/**
 * Remove the saved admin context. Called on admin logout.
 */
export const clearAdminContext = async (): Promise<void> => {
  try {
    await contextTable.delete(ADMIN_CONTEXT_KEY);
  } catch (error) {
    console.error('[adminContext] Failed to clear admin context:', error);
  }
};

/**
 * Migrate any existing admin context from localStorage to IndexedDB.
 * Call once on app startup, then the localStorage entry is removed.
 */
export const migrateAdminContextFromLocalStorage = async (): Promise<void> => {
  try {
    const raw = localStorage.getItem(ADMIN_CONTEXT_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as IAdminContext;
    if (parsed?.cache) {
      await saveAdminContext(parsed.cache);
    }
    localStorage.removeItem(ADMIN_CONTEXT_KEY);
  } catch (error) {
    console.error('[adminContext] Failed to migrate from localStorage:', error);
    localStorage.removeItem(ADMIN_CONTEXT_KEY);
  }
};
